/**
 * useSaveMachine — orchestrates the save lifecycle for the editor
 * (spec.md § Editor / Manual save, Conflict, Non-409 save failures).
 *
 * Responsibilities:
 * - Hold the local document (body) and the server baseline (body/version) the
 *   next write is based on.
 * - Drive the {@link saveStateReducer} state machine.
 * - Fire `onSave` immediately on `save()` (no debounce); the editor stays
 *   editable during the in-flight request. If the user typed while the PUT was
 *   in flight, the post-save state returns to `unsaved` rather than `saved`.
 * - On 409: enter `conflict`, stash the server version for the banner, and let
 *   the caller drive the "Reload theirs" flow via {@link reloadTheirs}.
 * - On non-409 failure: fire the error toast and return to `unsaved`.
 *
 * The hook is content-source agnostic — Milkdown reports body changes via
 * `setBody`. The hook never reaches into the editor; it returns `serverBody` so
 * the editor can replace its content when a conflict is resolved.
 */
import { useCallback, useReducer, useRef, useState } from "react";
import type { ConflictResponse } from "@shared/types/api";
import { copyToClipboard } from "./clipboard";
import { saveStateReducer, type SaveState } from "./saveState";
import type { OnConflict, OnSave, Pad } from "./types";

const SAVE_ERROR_MESSAGE = "Couldn't save. Check your connection and try again.";
const CONFLICT_COPY_MESSAGE = "Your edits copied to clipboard — server version loaded";

export interface UseSaveMachineOptions {
  pad: Pad;
  onSave: OnSave;
  onConflict?: OnConflict;
  /** Fire a dark-pill toast (injected from the ToastProvider). */
  showToast: (message: string) => void;
}

export interface UseSaveMachineResult {
  state: SaveState;
  body: string;
  /** Set when state is `conflict`: the server version the banner reports. */
  conflict: ConflictResponse | null;
  /**
   * Body to load into the editor after a conflict is resolved (or `null` when
   * there is nothing to apply). The editor reads this to replace its content.
   */
  serverBody: string | null;
  setBody: (body: string) => void;
  /** Fire a save immediately (Save button / Cmd+S). */
  save: () => void;
  /**
   * Resolve a conflict: copy the user's local body to the clipboard, toast,
   * then adopt the server version as the new baseline + content.
   */
  reloadTheirs: () => Promise<void>;
  /** Acknowledge that the editor has applied `serverBody`. */
  clearServerBody: () => void;
}

export function useSaveMachine({
  pad,
  onSave,
  onConflict,
  showToast,
}: UseSaveMachineOptions): UseSaveMachineResult {
  const [state, dispatch] = useReducer(saveStateReducer, "clean");

  const [body, setBodyState] = useState(pad.body);
  const [conflict, setConflict] = useState<ConflictResponse | null>(null);
  const [serverBody, setServerBody] = useState<string | null>(null);

  // The version the next write is based on. Updated on a successful save and
  // when a conflict is resolved by adopting the server version.
  const versionRef = useRef(pad.version);

  // Live mirrors so `save()` reads the latest content without re-creating the
  // callback on every keystroke. `dirtyDuringSaveRef` tracks whether the user
  // typed between firing the PUT and its resolution.
  const bodyRef = useRef(body);
  const dirtyDuringSaveRef = useRef(false);
  const savingRef = useRef(false);

  const setBody = useCallback((next: string) => {
    bodyRef.current = next;
    setBodyState(next);
    if (savingRef.current) dirtyDuringSaveRef.current = true;
    dispatch({ type: "EDIT" });
  }, []);

  const save = useCallback(() => {
    // Fire immediately; ignore re-entrant saves while one is in flight.
    if (savingRef.current) return;
    savingRef.current = true;
    dirtyDuringSaveRef.current = false;
    dispatch({ type: "SAVE" });

    const payload = {
      body: bodyRef.current,
      version: versionRef.current,
    };

    void (async () => {
      try {
        const result = await onSave(payload);
        if (result.status === "conflict") {
          setConflict(result.server);
          dispatch({ type: "SAVE_CONFLICT" });
          onConflict?.(result.server);
          return;
        }
        // 200 OK — adopt the new version as the next baseline.
        versionRef.current = result.pad.version;
        dispatch({
          type: "SAVE_SUCCESS",
          dirtySinceRequest: dirtyDuringSaveRef.current,
        });
      } catch {
        // Non-409 failure (network / 5xx). Toast + stay savable.
        showToast(SAVE_ERROR_MESSAGE);
        dispatch({ type: "SAVE_ERROR" });
      } finally {
        savingRef.current = false;
      }
    })();
  }, [onSave, onConflict, showToast]);

  const reloadTheirs = useCallback(async () => {
    const current = conflict;
    if (!current) return;

    // Copy the user's local body FIRST so no edit is lost, then toast, then
    // adopt the server version (spec.md § Editor / Conflict).
    try {
      await copyToClipboard(bodyRef.current);
      showToast(CONFLICT_COPY_MESSAGE);
    } catch {
      // Clipboard unavailable/denied — surface the same error pattern. The
      // server version is still loaded so the user can re-copy manually.
      showToast(SAVE_ERROR_MESSAGE);
    }

    // Adopt the server version as the new baseline and content.
    versionRef.current = current.version;
    bodyRef.current = current.body;
    setBodyState(current.body);
    setServerBody(current.body);
    setConflict(null);
    dispatch({ type: "CONFLICT_RESOLVED" });
  }, [conflict, showToast]);

  const clearServerBody = useCallback(() => setServerBody(null), []);

  return {
    state,
    body,
    conflict,
    serverBody,
    setBody,
    save,
    reloadTheirs,
    clearServerBody,
  };
}
